import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GetV1CompaniesCompanyIdInvoicesData,
  getV1CompaniesCompanyIdOrdersInvoicing,
  GetV1CompaniesCompanyIdOrdersInvoicingData,
  InvoiceableType,
  InvoiceCategoryFilter,
  InvoiceState,
  JobState,
  OrderState,
  RateType,
  SiteType,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { noop, omit } from 'lodash-es';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { OrderOption } from '~components/OrderBySelector/OrderBySelector';
import { FetchReason } from '~constants/enums';
import { Order } from '~hooks/useOrders/models';
import { extractPagination, Pagination } from '~services/pagination';

import {
  ALL_INVOICE_STATES,
  DriverPayQueryOrdersSort,
  INVOICES_QUERY_CONTEXT_PREFIX,
} from './constants';
import { makeDriverPayQueryContextKey } from './utils';

const derivePaginationQueryParams = (pageParam?: Pagination) => {
  return {
    'page[limit]': 20,
    'page[after]': pageParam?.after,
  };
};

const deriveFilterQueryParams = (filters: DriverPayQueryFilters) => {
  const {
    customerAccountIds,
    departmentIds,
    driverIds,
    dropoffSiteIds,
    endDate,
    equipmentOwnerCompanyIds,
    invoiceCategory,
    invoiceStates,
    orderStates,
    pickupSiteIds,
    prevailingWage,
    projectIds,
    requesterIds,
    rateTypes,
    searchQuery,
    startDate,
    vendorAccountIds,
    grouped,
  } = filters;

  const query: GetV1CompaniesCompanyIdOrdersInvoicingData['query'] = {
    'filter[invoice][invoiceable_type]': InvoiceableType.JOB,
    'filter[department_ids]': departmentIds?.length ? departmentIds : undefined,

    'filter[invoice][start_date]': startDate
      ? dayjs.tz(startDate).startOf('day').toISOString()
      : undefined,

    'filter[invoice][end_date]': endDate
      ? dayjs.tz(endDate).endOf('day').toISOString()
      : undefined,

    'filter[invoice][invoice_category]':
      invoiceCategory ?? InvoiceCategoryFilter.PAYABLES,

    'filter[invoice][states]': invoiceStates?.length ? invoiceStates : undefined,

    'filter[invoice][rate_types]': rateTypes?.length ? rateTypes : undefined,

    'filter[project_ids]': projectIds?.length ? projectIds : undefined,

    'filter[invoice][vendor_account_ids]': vendorAccountIds?.length
      ? vendorAccountIds
      : undefined,

    'filter[invoice][equipment][company_ids]': equipmentOwnerCompanyIds?.length
      ? equipmentOwnerCompanyIds
      : undefined,

    'filter[invoice][customer_account_ids]': customerAccountIds?.length
      ? customerAccountIds
      : undefined,

    'filter[invoice][prevailing_wage]':
      prevailingWage?.length === 1 ? prevailingWage[0] === 'true' : undefined,

    'filter[invoice][pickup_site_ids]': pickupSiteIds?.length ? pickupSiteIds : undefined,

    'filter[invoice][dropoff_site_ids]': dropoffSiteIds?.length
      ? dropoffSiteIds
      : undefined,

    'filter[requester_ids]': requesterIds?.length ? requesterIds : undefined,

    'filter[invoice][driver_ids]': driverIds?.length ? driverIds : undefined,

    'filter[states]': orderStates?.length ? orderStates : undefined,

    'search[invoice][datagrid]': searchQuery ?? undefined,

    'filter[invoice][grouped]': grouped,
  };

  return query;
};

const deriveSortQueryParam = (orderSort: OrderOption<DriverPayQueryOrdersSort>) => {
  const { orderBy, sortBy } = orderSort;

  return {
    'sort[account][name]':
      orderBy === DriverPayQueryOrdersSort.ACCOUNT_NAME ? sortBy : undefined,

    'sort[company][legal_name]':
      orderBy === DriverPayQueryOrdersSort.COMPANY_LEGAL_NAME ? sortBy : undefined,
    'sort[created_at]':
      orderBy === DriverPayQueryOrdersSort.CREATED_AT ? sortBy : undefined,

    'sort[friendly_order_id]':
      orderBy === DriverPayQueryOrdersSort.FRIENDLY_ORDER_ID ? sortBy : undefined,
  };
};

export const deriveInvoiceCsvExportQueryParams = (
  filters: DriverPayQueryFilters,
): NonNullable<GetV1CompaniesCompanyIdInvoicesData['query']> => {
  return {
    'filter[start_date]': filters.startDate
      ? dayjs.tz(filters.startDate).startOf('day').toISOString()
      : undefined,
    'filter[end_date]': filters.endDate
      ? dayjs.tz(filters.endDate).endOf('day').toISOString()
      : undefined,
    'filter[states]': filters.invoiceStates?.length ? filters.invoiceStates : undefined,
    'filter[rate_types]': filters.rateTypes?.length ? filters.rateTypes : undefined,
    'filter[project_ids]': filters.projectIds?.length ? filters.projectIds : undefined,
    'filter[customer_account_ids]': filters.customerAccountIds?.length
      ? filters.customerAccountIds
      : undefined,
    'filter[vendor_account_ids]': filters.vendorAccountIds?.length
      ? filters.vendorAccountIds
      : undefined,
    'filter[equipment][company_ids]': filters.equipmentOwnerCompanyIds?.length
      ? filters.equipmentOwnerCompanyIds
      : undefined,
    'filter[driver_ids]': filters.driverIds?.length ? filters.driverIds : undefined,
    'filter[pickup_site_ids]': filters.pickupSiteIds?.length
      ? filters.pickupSiteIds
      : undefined,
    'filter[dropoff_site_ids]': filters.dropoffSiteIds?.length
      ? filters.dropoffSiteIds
      : undefined,
    'filter[grouped]': filters.grouped,
  };
};

type DriverPayQueryFilters = {
  customerAccountIds?: string[];
  departmentIds?: string[];
  dispatchNumberIds?: string[];
  driverIds?: string[];
  dropoffSiteIds?: string[];
  endDate?: Dayjs;
  equipmentOwnerCompanyIds?: string[];
  equipmentTypeIds?: string[];
  foremanIds?: string[];
  invoiceCategory?: InvoiceCategoryFilter;
  invoiceStates?: InvoiceState[];
  jobStates?: JobState[];
  orderStates?: OrderState[];
  pickupSiteIds?: string[];
  prevailingWage?: string[];
  projectExternalIds?: string[];
  projectIds?: string[];
  rateTypes?: RateType[];
  requesterIds?: string[];
  searchQuery?: string;
  siteTypes?: SiteType[];
  startDate?: Dayjs;
  vendorAccountIds?: string[];
  grouped?: boolean;
  companyId?: string;
};

// Tab identity (invoiceCategory) and the Approved/Unapproved/In Settlement toggle
// (invoiceStates + grouped) don't count as active filters: clearing preserves the
// category and resets the toggle to its all-off default.
function hasActiveNonSearchFilters(filters: DriverPayQueryFilters) {
  const {
    companyId: _companyId,
    grouped: _grouped,
    invoiceCategory: _invoiceCategory,
    invoiceStates: _invoiceStates,
    searchQuery: _searchQuery,
    ...rest
  } = filters;

  return Object.values(rest).some(
    (value) => value !== undefined && !(Array.isArray(value) && value.length === 0),
  );
}

type DriverPayQueryParams = {
  filters: DriverPayQueryFilters;
  isRefreshing?: boolean;
  ordersSort: OrderOption<DriverPayQueryOrdersSort>;
  pagination?: Pagination;
};

const defaultOrdersSort: OrderOption<DriverPayQueryOrdersSort> = {
  orderBy: DriverPayQueryOrdersSort.CREATED_AT,
  sortBy: 'desc',
};

type DriverPayQueryContextType = {
  applyFilters: (filters: Partial<DriverPayQueryFilters>) => void;
  applyOrdersSort: (orderOption: OrderOption<DriverPayQueryOrdersSort>) => void;
  applySearchQuery: (searchQuery: string) => void;
  clear: () => void;
  clearAllFiltersExceptSearch: () => void;
  data: Order[];
  fetchMore: () => void;
  fetchReason?: FetchReason;
  filters: DriverPayQueryFilters;
  hasMore: boolean;
  hasUnfilteredResults: boolean;
  ordersSort: OrderOption<DriverPayQueryOrdersSort>;
  refresh: () => void;
};

const DriverPayQueryContext = createContext<DriverPayQueryContextType>({
  applyFilters: noop,
  applyOrdersSort: noop,
  applySearchQuery: noop,
  clear: noop,
  clearAllFiltersExceptSearch: noop,
  data: [],
  fetchMore: noop,
  filters: {},
  hasMore: false,
  hasUnfilteredResults: false,
  ordersSort: { ...defaultOrdersSort },
  refresh: noop,
});

function DriverPayQueryContextProvider({
  companyId,
  children,
}: React.PropsWithChildren<{ companyId: string }>) {
  const queryKey = makeDriverPayQueryContextKey();
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<DriverPayQueryParams>({
    filters: {},
    ordersSort: { ...{ ...defaultOrdersSort } },
  });

  const query = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters, queryParams.ordersSort],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: async ({ pageParam, signal }) => {
      const { filters, ordersSort } = queryParams;

      const query: GetV1CompaniesCompanyIdOrdersInvoicingData['query'] = {
        ...derivePaginationQueryParams(pageParam),
        ...deriveFilterQueryParams({ ...filters }),
        ...deriveSortQueryParam(ordersSort),
      };

      const response = await getV1CompaniesCompanyIdOrdersInvoicing({
        path: { 'company-id': companyId },
        query,
        signal,
      });

      const orders = response.data.data.map((order) => Order.parse(order));
      const pagination = extractPagination(response);

      setQueryParams((state) => ({ ...state, pagination }));

      return orders;
    },
  });

  const hasMore = Boolean(queryParams.pagination?.after);

  const data = useMemo(() => (query.data?.pages ?? []).flat(), [query.data?.pages]);

  const searchQuery = queryParams.filters.searchQuery ?? '';
  const invoiceCategory =
    queryParams.filters.invoiceCategory ?? InvoiceCategoryFilter.PAYABLES;
  const shouldProbeForUnfilteredResults =
    !query.isFetching &&
    !query.isError &&
    data.length === 0 &&
    searchQuery.trim().length > 0 &&
    hasActiveNonSearchFilters(queryParams.filters);

  // Probe = the exact query the screen would issue right after clearing filters
  // (search + preserved category + the all-off toggle default), capped at one row.
  // Powers the "Clear filters and search again" hint. Failures stay
  // silent: the hint simply doesn't show. Every varying probe input is in the key,
  // so Payables/Receivables never share a cache entry.
  const unfilteredResultsProbe = useQuery({
    enabled: shouldProbeForUnfilteredResults,
    queryKey: [queryKey, 'unfiltered-probe', companyId, invoiceCategory, searchQuery],
    queryFn: async ({ signal }) => {
      const probeQuery: GetV1CompaniesCompanyIdOrdersInvoicingData['query'] = {
        'page[limit]': 1,
        ...deriveFilterQueryParams({
          invoiceCategory,
          invoiceStates: [...ALL_INVOICE_STATES],
          searchQuery,
        }),
      };

      const response = await getV1CompaniesCompanyIdOrdersInvoicing({
        path: { 'company-id': companyId },
        query: probeQuery,
        signal,
      });

      return response.data.data.length > 0;
    },
    refetchOnWindowFocus: false,
    retry: false,
    // Re-searching the same term within 30s reuses the cached probe answer
    // instead of issuing another request.
    staleTime: 30_000,
  });

  const hasUnfilteredResults = shouldProbeForUnfilteredResults
    ? (unfilteredResultsProbe.data ?? false)
    : false;

  const fetchReason = useMemo(() => {
    if (query.isFetchingNextPage) {
      return FetchReason.INFINITE_SCROLL;
    }

    if (query.isFetching) {
      return FetchReason.INITIAL_FETCH;
    }
  }, [query.isFetchingNextPage, query.isFetching]);

  const fetchMore = useCallback(() => {
    if (hasMore) {
      query.fetchNextPage();
    }
  }, [hasMore]);

  const clear = useCallback(() => {
    queryClient.removeQueries({
      predicate(query) {
        return query.queryKey.some((key) => {
          const keyAsString = String(key);

          return (
            keyAsString.startsWith(INVOICES_QUERY_CONTEXT_PREFIX) ||
            keyAsString.startsWith(queryKey)
          );
        });
      },
    });
  }, [queryKey]);

  const applyFilters = useCallback<DriverPayQueryContextType['applyFilters']>(
    (filters) => {
      clear();
      setQueryParams((state) => ({
        filters: { ...state.filters, ...filters },
        ordersSort: { ...state.ordersSort },
      }));
    },
    [clear],
  );

  const applySearchQuery = useCallback<DriverPayQueryContextType['applySearchQuery']>(
    (searchQuery) => {
      clear();
      setQueryParams((state) => ({
        filters: { ...state.filters, searchQuery: searchQuery || undefined },
        ordersSort: { ...state.ordersSort },
      }));
    },
    [clear],
  );

  const applyOrdersSort = useCallback<DriverPayQueryContextType['applyOrdersSort']>(
    (orderOption) => {
      clear();
      setQueryParams((state) => ({
        filters: { ...state.filters },
        ordersSort: orderOption,
      }));
    },
    [clear],
  );

  const clearAllFiltersExceptSearch = useCallback<
    DriverPayQueryContextType['clearAllFiltersExceptSearch']
  >(() => {
    clear();
    // Replace (not merge): only the search term, the category tab, and the sort
    // survive. The state toggle resets to its all-off default (every invoice
    // state, grouped omitted), matching what InvoiceStateFilters derives once the
    // toggle URL params are deleted — the follow-up URL sync becomes a no-op.
    setQueryParams((state) => ({
      filters: {
        invoiceCategory: state.filters.invoiceCategory,
        invoiceStates: [...ALL_INVOICE_STATES],
        searchQuery: state.filters.searchQuery,
      },
      ordersSort: { ...state.ordersSort },
    }));
  }, [clear]);

  const refresh = useCallback<DriverPayQueryContextType['refresh']>(() => {
    setQueryParams((state) => ({
      filters: { ...state.filters },
      ordersSort: { ...state.ordersSort },
      isRefreshing: true,
    }));
  }, [clear]);

  useEffect(() => {
    if (queryParams.isRefreshing) {
      setQueryParams((state) => omit(state, 'isRefreshing'));

      clear();
      query.refetch();
    }
  }, [queryParams.isRefreshing, clear]);

  return (
    <DriverPayQueryContext.Provider
      value={{
        applyFilters,
        applyOrdersSort,
        applySearchQuery,
        clear,
        clearAllFiltersExceptSearch,
        data,
        fetchMore,
        fetchReason,
        filters: queryParams.filters,
        hasMore,
        hasUnfilteredResults,
        ordersSort: queryParams.ordersSort,
        refresh,
      }}
    >
      {children}
    </DriverPayQueryContext.Provider>
  );
}

export default DriverPayQueryContext;
export { DriverPayQueryContextProvider };
